import React, { useState } from 'react';
import api from '../../services/api';
import useScreenSize from '../../hooks/useScreenSize';

function AISuggestions({ noteContent, noteTitle, onInsertSuggestion }) {
  const { isMobile } = useScreenSize();
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const fetchSuggestions = async () => {
    // Clear previous state
    setError('');
    setSuggestions([]);
    setIsLoading(true);
    setIsExpanded(true);

    try {
      const response = await api.post('/ai/suggest', {
        content: noteContent,
        title: noteTitle,
      });

      if (response.data.success) {
        setSuggestions(response.data.suggestions);
      } else {
        setError(response.data.error || 'Failed to generate suggestions');
      }
    } catch (err) {
      console.error('Error fetching AI suggestions:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.code === 'ECONNABORTED') {
        setError('Request timed out. The AI service may be slow or unavailable.');
      } else {
        setError('Failed to generate suggestions. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInsert = (suggestion) => {
    if (onInsertSuggestion) {
      onInsertSuggestion(suggestion);
    }
  };

  return (
    <div style={styles.container}>
      {!isExpanded ? (
        <button
          type="button"
          onClick={fetchSuggestions}
          className="internal-primary-action"
          style={{
            ...styles.triggerButton,
            minHeight: isMobile ? '44px' : 'auto',
            padding: isMobile ? '12px 24px' : '10px 20px',
          }}
          disabled={isLoading}
        >
          ✨ Get AI Suggestions
        </button>
      ) : (
        <div
          style={{
            ...styles.panel,
            maxWidth: isMobile ? 'none' : '800px',
            padding: isMobile ? '12px' : '16px',
          }}
        >
          <div style={styles.header}>
            <h3 style={styles.title}>AI Suggestions</h3>
            <button
              type="button"
              className="icon-action"
              onClick={() => setIsExpanded(false)}
              style={styles.closeButton}
              aria-label="Close AI suggestions"
            >
              ✕
            </button>
          </div>

          {isLoading && (
            <div style={styles.loading}>
              <div style={styles.spinner}></div>
              <p style={styles.loadingText}>Generating suggestions...</p>
            </div>
          )}

          {error && (
            <div style={styles.error}>
              <p style={styles.errorText}>{error}</p>
              <button
                type="button"
                onClick={fetchSuggestions}
                className="internal-primary-action"
                style={{
                  ...styles.retryButton,
                  minHeight: isMobile ? '44px' : 'auto',
                  padding: isMobile ? '12px 24px' : '8px 16px',
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {!isLoading && !error && suggestions.length > 0 && (
            <div style={styles.suggestions}>
              <p style={styles.instructions}>
                Click on a suggestion to add it to your note:
              </p>
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.suggestionCard,
                    flexDirection: isMobile ? 'column' : 'row',
                  }}
                >
                  <div style={styles.suggestionNumber}>{index + 1}</div>
                  <div style={styles.suggestionContent}>
                    <p style={styles.suggestionText}>{suggestion}</p>
                    <button
                      type="button"
                      onClick={() => handleInsert(suggestion)}
                      className="internal-secondary-action"
                      style={{
                        ...styles.insertButton,
                        width: isMobile ? '100%' : 'auto',
                        minHeight: '44px',
                      }}
                    >
                      Insert
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={fetchSuggestions}
                className="internal-secondary-action"
                style={{
                  ...styles.refreshButton,
                  minHeight: isMobile ? '44px' : 'auto',
                  padding: isMobile ? '12px 20px' : '10px 16px',
                }}
              >
                🔄 Generate New Suggestions
              </button>
            </div>
          )}

          {!isLoading && !error && suggestions.length === 0 && (
            <div style={styles.empty}>
              <p style={styles.emptyText}>No suggestions generated yet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    marginTop: '16px',
  },
  triggerButton: {
    background: '#0778ad',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'transform 0.2s',
  },
  panel: {
    background: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '2px solid #f0f0f0',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
  },
  closeButton: {
    background: '#f8fcfe',
    border: '1px solid #d9e9f2',
    borderRadius: '6px',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#52677c',
    padding: '4px 8px',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #0778ad',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    color: '#666',
    fontSize: '14px',
  },
  error: {
    background: '#fee',
    border: '1px solid #fcc',
    borderRadius: '8px',
    padding: '16px',
    textAlign: 'center',
  },
  errorText: {
    color: '#c33',
    margin: '0 0 12px 0',
    fontSize: '14px',
  },
  retryButton: {
    background: '#0778ad',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  suggestions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  instructions: {
    color: '#666',
    fontSize: '13px',
    margin: '0 0 8px 0',
    fontStyle: 'italic',
  },
  suggestionCard: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    background: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #e8e8e8',
  },
  suggestionNumber: {
    width: '28px',
    height: '28px',
    background: '#0778ad',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  suggestionContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  suggestionText: {
    margin: 0,
    fontSize: '14px',
    color: '#333',
    lineHeight: '1.5',
  },
  insertButton: {
    alignSelf: 'flex-start',
    background: 'white',
    border: '1px solid #0778ad',
    color: '#0778ad',
    padding: '6px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  refreshButton: {
    background: 'white',
    border: '1px solid #ddd',
    color: '#333',
    padding: '10px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  empty: {
    padding: '24px',
    textAlign: 'center',
  },
  emptyText: {
    color: '#999',
    margin: 0,
  },
};

export default AISuggestions;
